import { createFileRoute } from "@tanstack/react-router";
import { useEffect, useMemo, useState, type ReactNode } from "react";
import { AppShell } from "@/components/app-shell";
import { requireRole } from "@/lib/session";
import { supabase } from "@/integrations/supabase/client";
import { useLearnerContext } from "@/lib/learner-context";
import { useServerFn } from "@tanstack/react-start";
import { generateCareerRecommendations, type CareerResult } from "@/lib/portfolio.functions";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogTrigger } from "@/components/ui/dialog";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { Switch } from "@/components/ui/switch";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import {
  Award, GraduationCap, Trophy, FolderGit2, Image as ImageIcon, Video as VideoIcon,
  Sparkles, Wrench, Medal, HeartHandshake, FileText, Building2, Briefcase, Plus,
  Download, Share2, Loader2, Pencil, Trash2, Star, ExternalLink, User,
} from "lucide-react";
import { cn } from "@/lib/utils";

export const Route = createFileRoute("/learner/portfolio")({
  ssr: false,
  beforeLoad: () => requireRole("learner"),
  head: () => ({ meta: [{ title: "My Portfolio — MAKAI" }] }),
  component: PortfolioPage,
});

type Kind =
  | "certificate" | "award" | "project" | "photo" | "video" | "achievement"
  | "skill" | "competition" | "volunteer" | "report_card"
  | "university_application" | "employment";

const KINDS: { id: Kind; label: string; icon: typeof Award; tint: string }[] = [
  { id: "certificate",           label: "Certificates",         icon: Award,         tint: "from-amber-500/20 to-amber-500/5" },
  { id: "award",                 label: "Awards",               icon: Trophy,        tint: "from-yellow-500/20 to-yellow-500/5" },
  { id: "project",               label: "Projects",             icon: FolderGit2,    tint: "from-violet-500/20 to-violet-500/5" },
  { id: "photo",                 label: "Photos",               icon: ImageIcon,     tint: "from-rose-500/20 to-rose-500/5" },
  { id: "video",                 label: "Videos",               icon: VideoIcon,     tint: "from-red-500/20 to-red-500/5" },
  { id: "achievement",           label: "Achievements",         icon: Sparkles,      tint: "from-sky-500/20 to-sky-500/5" },
  { id: "skill",                 label: "Skills",               icon: Wrench,        tint: "from-emerald-500/20 to-emerald-500/5" },
  { id: "competition",           label: "Competitions",         icon: Medal,         tint: "from-orange-500/20 to-orange-500/5" },
  { id: "volunteer",             label: "Volunteer Work",       icon: HeartHandshake,tint: "from-pink-500/20 to-pink-500/5" },
  { id: "report_card",           label: "Report Cards",         icon: FileText,      tint: "from-blue-500/20 to-blue-500/5" },
  { id: "university_application",label: "University Applications", icon: Building2,  tint: "from-indigo-500/20 to-indigo-500/5" },
  { id: "employment",            label: "Employment Readiness", icon: Briefcase,     tint: "from-teal-500/20 to-teal-500/5" },
];

interface Item {
  id: string;
  learner_id: string;
  kind: Kind;
  title: string;
  description: string | null;
  issuer: string | null;
  date_awarded: string | null;
  date_start: string | null;
  date_end: string | null;
  location: string | null;
  url: string | null;
  media_path: string | null;
  media_mime: string | null;
  skills: string[] | null;
  tags: string[] | null;
  visibility: "private" | "school" | "public";
  is_featured: boolean;
  created_at: string;
}

interface Settings {
  learner_id: string;
  headline: string | null;
  bio: string | null;
  contact_email: string | null;
  contact_phone: string | null;
  website: string | null;
  linkedin: string | null;
  github: string | null;
  public_slug: string | null;
  is_public: boolean;
}

interface CareerRow extends CareerResult { id: string; generated_at: string }

function PortfolioPage() {
  const { ctx, loading: ctxLoading } = useLearnerContext();
  const [items, setItems] = useState<Item[]>([]);
  const [settings, setSettings] = useState<Settings | null>(null);
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState<Kind | "all">("all");
  const [openAdd, setOpenAdd] = useState(false);
  const [editing, setEditing] = useState<Item | null>(null);
  const [careerRec, setCareerRec] = useState<CareerRow | null>(null);
  const [genLoading, setGenLoading] = useState(false);
  const [profileOpen, setProfileOpen] = useState(false);
  const genCareer = useServerFn(generateCareerRecommendations);

  useEffect(() => {
    if (!ctx) return;
    let mounted = true;
    (async () => {
      const [it, st, cr] = await Promise.all([
        supabase.from("portfolio_items").select("*").eq("learner_id", ctx.learnerId).order("date_awarded", { ascending: false, nullsFirst: false }).order("created_at", { ascending: false }),
        supabase.from("portfolio_settings").select("*").eq("learner_id", ctx.learnerId).maybeSingle(),
        supabase.from("portfolio_career_recommendations").select("*").eq("learner_id", ctx.learnerId).order("generated_at", { ascending: false }).limit(1).maybeSingle(),
      ]);
      if (!mounted) return;
      setItems((it.data ?? []) as Item[]);
      if (st.data) setSettings(st.data as Settings);
      else {
        const { data: created } = await supabase
          .from("portfolio_settings")
          .insert({ learner_id: ctx.learnerId })
          .select("*")
          .single();
        if (created) setSettings(created as Settings);
      }
      if (cr.data) setCareerRec(cr.data as unknown as CareerRow);
      setLoading(false);
    })();
    return () => { mounted = false; };
  }, [ctx]);

  const counts = useMemo(() => {
    const c: Record<string, number> = {};
    for (const i of items) c[i.kind] = (c[i.kind] ?? 0) + 1;
    return c;
  }, [items]);

  const filtered = useMemo(() => tab === "all" ? items : items.filter((i) => i.kind === tab), [items, tab]);

  const featured = useMemo(() => items.filter((i) => i.is_featured).slice(0, 4), [items]);

  async function refreshItems() {
    if (!ctx) return;
    const { data } = await supabase.from("portfolio_items").select("*").eq("learner_id", ctx.learnerId).order("date_awarded", { ascending: false, nullsFirst: false }).order("created_at", { ascending: false });
    setItems((data ?? []) as Item[]);
  }

  async function handleDelete(id: string) {
    if (!confirm("Delete this portfolio item?")) return;
    const { error } = await supabase.from("portfolio_items").delete().eq("id", id);
    if (error) toast.error(error.message); else { toast.success("Deleted"); refreshItems(); }
  }

  async function toggleFeatured(it: Item) {
    const { error } = await supabase.from("portfolio_items").update({ is_featured: !it.is_featured }).eq("id", it.id);
    if (error) toast.error(error.message); else refreshItems();
  }

  async function generateAI() {
    if (!ctx) return;
    setGenLoading(true);
    try {
      // pull subject averages
      const { data: marks } = await supabase
        .from("assessment_marks")
        .select("score, assessments(max_score, subjects_catalog(name))")
        .eq("learner_id", ctx.learnerId);
      const map: Record<string, { sum: number; max: number }> = {};
      type Row = { score: number | null; assessments: { max_score: number | null; subjects_catalog: { name: string } | null } | null };
      for (const m of (marks ?? []) as unknown as Row[]) {
        const name = m.assessments?.subjects_catalog?.name;
        const max = m.assessments?.max_score;
        if (!name || m.score == null || !max) continue;
        map[name] = map[name] ?? { sum: 0, max: 0 };
        map[name].sum += m.score; map[name].max += max;
      }
      const subjectAverages = Object.entries(map).map(([subject, v]) => ({ subject, average: (v.sum / v.max) * 100 }));
      const allSkills = Array.from(new Set(items.flatMap((i) => i.skills ?? [])));
      const result = await genCareer({
        data: {
          learnerName: `${ctx.firstName ?? "Student"} ${ctx.lastName ?? ""}`.trim(),
          gradeLevel: ctx.gradeLevel,
          bio: settings?.bio ?? null,
          skills: allSkills,
          subjectAverages,
          items: items.map((i) => ({ kind: i.kind, title: i.title, description: i.description })),
        },
      });
      if (result.error) {
        toast.error(result.message ?? "AI failed");
      } else {
        const { data: row } = await supabase.from("portfolio_career_recommendations").insert({
          learner_id: ctx.learnerId,
          summary: result.summary,
          careers: result.careers as unknown as never,
          strengths: result.strengths as unknown as never,
          growth_areas: result.growth_areas as unknown as never,
          next_steps: result.next_steps as unknown as never,
        }).select("*").single();
        if (row) setCareerRec(row as unknown as CareerRow);
        toast.success("Career recommendations ready");
      }
    } finally { setGenLoading(false); }
  }

  async function copyShare() {
    if (!settings?.public_slug) {
      toast.error("Set a public link first in profile settings.");
      return;
    }
    const url = `${window.location.origin}/p/${settings.public_slug}`;
    await navigator.clipboard.writeText(url);
    toast.success("Share link copied");
  }

  async function exportPDF() {
    if (!ctx) return;
    const jsPDF = (await import("jspdf")).default;
    const doc = new jsPDF({ unit: "pt", format: "a4" });
    const W = doc.internal.pageSize.getWidth(); const M = 48; let y = 56;
    const name = `${ctx.firstName ?? "Student"} ${ctx.lastName ?? ""}`.trim();
    doc.setFont("helvetica", "bold"); doc.setFontSize(24); doc.text(name || "Student Portfolio", M, y); y += 8;
    doc.setLineWidth(2); doc.setDrawColor(200, 160, 60); doc.line(M, y, M + 60, y); y += 22;
    doc.setFont("helvetica", "normal"); doc.setFontSize(11); doc.setTextColor(80);
    if (settings?.headline) { doc.text(settings.headline, M, y); y += 16; }
    if (settings?.bio) {
      const lines = doc.splitTextToSize(settings.bio, W - M * 2);
      doc.text(lines, M, y); y += lines.length * 13 + 6;
    }
    doc.setTextColor(120); doc.setFontSize(9);
    const contact = [settings?.contact_email, settings?.contact_phone, settings?.website, settings?.linkedin].filter(Boolean).join(" • ");
    if (contact) { doc.text(contact, M, y); y += 18; }
    doc.setTextColor(20);

    const addSection = (title: string, rows: Item[]) => {
      if (!rows.length) return;
      if (y > 740) { doc.addPage(); y = 56; }
      doc.setFont("helvetica", "bold"); doc.setFontSize(13); doc.setTextColor(11, 23, 54);
      doc.text(title.toUpperCase(), M, y); y += 4;
      doc.setDrawColor(11, 23, 54); doc.setLineWidth(0.6); doc.line(M, y, W - M, y); y += 16;
      doc.setTextColor(20);
      for (const r of rows) {
        if (y > 770) { doc.addPage(); y = 56; }
        doc.setFont("helvetica", "bold"); doc.setFontSize(11);
        doc.text(r.title, M, y);
        const meta = [r.issuer, r.date_awarded ?? r.date_end ?? r.date_start, r.location].filter(Boolean).join(" • ");
        if (meta) {
          doc.setFont("helvetica", "normal"); doc.setFontSize(9); doc.setTextColor(110);
          doc.text(meta, W - M, y, { align: "right" }); doc.setTextColor(20);
        }
        y += 14;
        if (r.description) {
          doc.setFont("helvetica", "normal"); doc.setFontSize(10);
          const lines = doc.splitTextToSize(r.description, W - M * 2);
          doc.text(lines, M, y); y += lines.length * 12;
        }
        if (r.skills?.length) {
          doc.setFontSize(9); doc.setTextColor(120);
          doc.text(`Skills: ${r.skills.join(", ")}`, M, y); doc.setTextColor(20); y += 12;
        }
        y += 6;
      }
      y += 8;
    };

    for (const k of KINDS) {
      const rows = items.filter((i) => i.kind === k.id);
      addSection(k.label, rows);
    }

    if (careerRec) {
      if (y > 700) { doc.addPage(); y = 56; }
      doc.setFont("helvetica", "bold"); doc.setFontSize(13); doc.setTextColor(11, 23, 54);
      doc.text("AI CAREER RECOMMENDATIONS", M, y); y += 4;
      doc.line(M, y, W - M, y); y += 16; doc.setTextColor(20);
      doc.setFont("helvetica", "normal"); doc.setFontSize(10);
      const sumLines = doc.splitTextToSize(careerRec.summary, W - M * 2);
      doc.text(sumLines, M, y); y += sumLines.length * 12 + 6;
      for (const c of careerRec.careers) {
        if (y > 760) { doc.addPage(); y = 56; }
        doc.setFont("helvetica", "bold"); doc.setFontSize(11);
        doc.text(`${c.title}  (${c.match}% match)`, M, y); y += 13;
        doc.setFont("helvetica", "normal"); doc.setFontSize(10);
        const whyLines = doc.splitTextToSize(`${c.why}  Pathway: ${c.pathway}`, W - M * 2);
        doc.text(whyLines, M, y); y += whyLines.length * 12 + 6;
      }
    }

    doc.save(`${(name || "portfolio").replace(/\s+/g, "_")}_portfolio.pdf`);
  }

  if (ctxLoading || loading) {
    return <AppShell role="learner"><div className="p-6 flex items-center gap-2 text-muted-foreground"><Loader2 className="size-4 animate-spin" /> Loading portfolio…</div></AppShell>;
  }
  if (!ctx) {
    return <AppShell role="learner"><div className="p-6">Create a learner profile to start your portfolio.</div></AppShell>;
  }

  return (
    <AppShell role="learner">
      <div className="p-4 md:p-6 space-y-6 max-w-6xl mx-auto">
        {/* Header */}
        <div className="rounded-2xl border bg-gradient-to-br from-primary/10 via-background to-background p-5 md:p-6">
          <div className="flex flex-col md:flex-row md:items-start gap-4 md:gap-6">
            <div className="size-20 md:size-24 rounded-2xl bg-primary/15 text-primary flex items-center justify-center text-2xl font-bold border">
              {(ctx.firstName?.[0] ?? "S") + (ctx.lastName?.[0] ?? "")}
            </div>
            <div className="flex-1 min-w-0">
              <div className="flex flex-wrap items-center gap-2">
                <h1 className="text-2xl md:text-3xl font-bold tracking-tight">{ctx.firstName} {ctx.lastName}</h1>
                {settings?.is_public && <Badge variant="secondary" className="gap-1"><Share2 className="size-3" />Public</Badge>}
              </div>
              <p className="text-muted-foreground mt-1">{settings?.headline || "Lifelong learning portfolio"}</p>
              {settings?.bio && <p className="mt-3 text-sm leading-relaxed line-clamp-3 max-w-2xl">{settings.bio}</p>}
              <div className="mt-4 flex flex-wrap gap-2">
                <Button onClick={() => { setEditing(null); setOpenAdd(true); }} className="gap-2"><Plus className="size-4" />Add item</Button>
                <Button variant="outline" onClick={() => setProfileOpen(true)} className="gap-2"><User className="size-4" />Edit profile</Button>
                <Button variant="outline" onClick={exportPDF} className="gap-2"><Download className="size-4" />Export PDF</Button>
                <Button variant="outline" onClick={copyShare} className="gap-2"><Share2 className="size-4" />Share</Button>
              </div>
            </div>
          </div>

          {/* Stats */}
          <div className="grid grid-cols-3 md:grid-cols-6 gap-2 mt-5">
            {KINDS.slice(0, 6).map((k) => (
              <button key={k.id} onClick={() => setTab(k.id)} className={cn("rounded-xl border p-3 text-left bg-gradient-to-br", k.tint, "hover:scale-[1.02] transition")}>
                <k.icon className="size-4 mb-1" />
                <div className="text-2xl font-bold leading-none">{counts[k.id] ?? 0}</div>
                <div className="text-[11px] text-muted-foreground mt-1">{k.label}</div>
              </button>
            ))}
          </div>
        </div>

        {/* Featured */}
        {featured.length > 0 && (
          <section>
            <h2 className="text-sm font-semibold text-muted-foreground mb-2 flex items-center gap-2"><Star className="size-4 text-amber-500" />Featured</h2>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
              {featured.map((it) => <ItemCard key={it.id} item={it} onEdit={() => { setEditing(it); setOpenAdd(true); }} onDelete={() => handleDelete(it.id)} onFeature={() => toggleFeatured(it)} />)}
            </div>
          </section>
        )}

        {/* AI Career */}
        <section className="rounded-2xl border bg-card p-5">
          <div className="flex items-start justify-between gap-3">
            <div>
              <h2 className="text-lg font-semibold flex items-center gap-2"><Sparkles className="size-4 text-primary" />AI Career Recommendations</h2>
              <p className="text-sm text-muted-foreground">Personalised pathways based on your skills, grades and portfolio.</p>
            </div>
            <Button onClick={generateAI} disabled={genLoading} className="gap-2">
              {genLoading ? <Loader2 className="size-4 animate-spin" /> : <Sparkles className="size-4" />}
              {careerRec ? "Regenerate" : "Generate"}
            </Button>
          </div>
          {careerRec && (
            <div className="mt-4 space-y-4">
              <p className="text-sm leading-relaxed">{careerRec.summary}</p>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
                {careerRec.careers.map((c, i) => (
                  <div key={i} className="rounded-xl border p-3 bg-background">
                    <div className="flex items-center justify-between gap-2">
                      <h3 className="font-semibold text-sm">{c.title}</h3>
                      <Badge variant="secondary">{c.match}%</Badge>
                    </div>
                    <p className="text-xs text-muted-foreground mt-1">{c.why}</p>
                    <p className="text-xs mt-2"><span className="font-medium">Path:</span> {c.pathway}</p>
                  </div>
                ))}
              </div>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                <RecList title="Strengths" items={careerRec.strengths} tint="emerald" />
                <RecList title="Growth areas" items={careerRec.growth_areas} tint="amber" />
                <RecList title="Next steps" items={careerRec.next_steps} tint="sky" />
              </div>
            </div>
          )}
        </section>

        {/* Tabs */}
        <div className="sticky top-0 z-10 -mx-4 px-4 md:mx-0 md:px-0 bg-background/80 backdrop-blur border-b pb-2">
          <div className="flex gap-2 overflow-x-auto">
            <TabChip active={tab === "all"} onClick={() => setTab("all")}>All ({items.length})</TabChip>
            {KINDS.map((k) => (
              <TabChip key={k.id} active={tab === k.id} onClick={() => setTab(k.id)}>
                <k.icon className="size-3.5" />{k.label} ({counts[k.id] ?? 0})
              </TabChip>
            ))}
          </div>
        </div>

        {/* Items grid */}
        {filtered.length === 0 ? (
          <div className="rounded-xl border border-dashed p-10 text-center text-muted-foreground">
            Nothing here yet. Click <span className="font-medium text-foreground">Add item</span> to begin your portfolio.
          </div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
            {filtered.map((it) => <ItemCard key={it.id} item={it} onEdit={() => { setEditing(it); setOpenAdd(true); }} onDelete={() => handleDelete(it.id)} onFeature={() => toggleFeatured(it)} />)}
          </div>
        )}

        <ItemDialog
          open={openAdd}
          onOpenChange={(v) => { setOpenAdd(v); if (!v) setEditing(null); }}
          learnerId={ctx.learnerId}
          userId={ctx.learnerId /* note: storage uses auth uid */}
          editing={editing}
          onSaved={refreshItems}
        />

        <ProfileDialog open={profileOpen} onOpenChange={setProfileOpen} settings={settings} onSaved={(s) => setSettings(s)} learnerId={ctx.learnerId} />
      </div>
    </AppShell>
  );
}

function TabChip({ active, onClick, children }: { active: boolean; onClick: () => void; children: ReactNode }) {
  return (
    <button onClick={onClick} className={cn("shrink-0 inline-flex items-center gap-1.5 rounded-full border px-3 py-1.5 text-xs font-medium transition", active ? "bg-primary text-primary-foreground border-primary" : "bg-card hover:bg-muted")}>
      {children}
    </button>
  );
}

function RecList({ title, items, tint }: { title: string; items: string[]; tint: "emerald" | "amber" | "sky" }) {
  const cls = { emerald: "border-emerald-500/30 bg-emerald-500/5", amber: "border-amber-500/30 bg-amber-500/5", sky: "border-sky-500/30 bg-sky-500/5" }[tint];
  return (
    <div className={cn("rounded-xl border p-3", cls)}>
      <h4 className="text-xs font-semibold uppercase tracking-wide mb-2">{title}</h4>
      <ul className="space-y-1.5 text-sm">
        {items.map((s, i) => <li key={i} className="flex gap-2"><span className="text-muted-foreground">•</span><span>{s}</span></li>)}
      </ul>
    </div>
  );
}

function ItemCard({ item, onEdit, onDelete, onFeature }: { item: Item; onEdit: () => void; onDelete: () => void; onFeature: () => void }) {
  const k = KINDS.find((x) => x.id === item.kind);
  const Icon = k?.icon ?? Award;
  const [signed, setSigned] = useState<string | null>(null);
  useEffect(() => {
    if (!item.media_path) return;
    let cancelled = false;
    supabase.storage.from("portfolio-media").createSignedUrl(item.media_path, 3600).then(({ data }) => {
      if (!cancelled && data?.signedUrl) setSigned(data.signedUrl);
    });
    return () => { cancelled = true; };
  }, [item.media_path]);
  const isImage = item.media_mime?.startsWith("image/");
  const isVideo = item.media_mime?.startsWith("video/");
  return (
    <div className={cn("group rounded-xl border bg-gradient-to-br p-4 hover:shadow-md transition flex flex-col", k?.tint)}>
      {signed && isImage && <img loading="lazy" decoding="async" src={signed} alt={item.title} className="rounded-lg mb-3 aspect-video object-cover bg-muted" />}
      {signed && isVideo && <video src={signed} controls className="rounded-lg mb-3 aspect-video bg-black" />}
      <div className="flex items-start gap-3">
        <div className="size-9 rounded-lg bg-background border flex items-center justify-center shrink-0"><Icon className="size-4" /></div>
        <div className="flex-1 min-w-0">
          <h3 className="font-semibold leading-tight">{item.title}</h3>
          {item.issuer && <p className="text-xs text-muted-foreground mt-0.5">{item.issuer}</p>}
        </div>
        <button onClick={onFeature} title="Feature" className="opacity-60 hover:opacity-100"><Star className={cn("size-4", item.is_featured && "fill-amber-500 text-amber-500 opacity-100")} /></button>
      </div>
      {item.description && <p className="text-sm text-muted-foreground mt-2 line-clamp-3">{item.description}</p>}
      <div className="mt-3 flex flex-wrap gap-1.5">
        {(item.date_awarded ?? item.date_end ?? item.date_start) && <Badge variant="outline" className="text-[10px]">{item.date_awarded ?? item.date_end ?? item.date_start}</Badge>}
        {item.skills?.slice(0, 3).map((s) => <Badge key={s} variant="secondary" className="text-[10px]">{s}</Badge>)}
      </div>
      <div className="mt-3 pt-3 border-t flex items-center justify-between text-xs">
        {item.url ? <a href={item.url} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1 text-primary hover:underline"><ExternalLink className="size-3" />Link</a> : <span className="text-muted-foreground capitalize">{item.visibility}</span>}
        <div className="flex gap-1 opacity-0 group-hover:opacity-100 transition">
          <Button size="icon" variant="ghost" className="size-7" onClick={onEdit}><Pencil className="size-3.5" /></Button>
          <Button size="icon" variant="ghost" className="size-7 text-destructive" onClick={onDelete}><Trash2 className="size-3.5" /></Button>
        </div>
      </div>
    </div>
  );
}

function ItemDialog({ open, onOpenChange, learnerId, editing, onSaved }: {
  open: boolean; onOpenChange: (v: boolean) => void; learnerId: string; userId: string;
  editing: Item | null; onSaved: () => void;
}) {
  const [kind, setKind] = useState<Kind>("certificate");
  const [title, setTitle] = useState("");
  const [issuer, setIssuer] = useState("");
  const [description, setDescription] = useState("");
  const [dateAwarded, setDateAwarded] = useState("");
  const [location, setLocation] = useState("");
  const [url, setUrl] = useState("");
  const [skills, setSkills] = useState("");
  const [visibility, setVisibility] = useState<"private" | "school" | "public">("private");
  const [file, setFile] = useState<File | null>(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!open) return;
    if (editing) {
      setKind(editing.kind); setTitle(editing.title); setIssuer(editing.issuer ?? "");
      setDescription(editing.description ?? ""); setDateAwarded(editing.date_awarded ?? "");
      setLocation(editing.location ?? ""); setUrl(editing.url ?? "");
      setSkills((editing.skills ?? []).join(", "));
      setVisibility(editing.visibility); setFile(null);
    } else {
      setKind("certificate"); setTitle(""); setIssuer(""); setDescription("");
      setDateAwarded(""); setLocation(""); setUrl(""); setSkills(""); setVisibility("private"); setFile(null);
    }
  }, [open, editing]);

  async function save() {
    if (!title.trim()) { toast.error("Title required"); return; }
    setSaving(true);
    try {
      let media_path = editing?.media_path ?? null;
      let media_mime = editing?.media_mime ?? null;
      if (file) {
        const { data: { user } } = await supabase.auth.getUser();
        if (!user) throw new Error("Not signed in");
        const ext = file.name.split(".").pop() ?? "bin";
        const path = `${user.id}/${learnerId}/${Date.now()}-${Math.random().toString(36).slice(2, 8)}.${ext}`;
        const { error } = await supabase.storage.from("portfolio-media").upload(path, file, { upsert: false });
        if (error) throw error;
        media_path = path; media_mime = file.type;
      }
      const payload = {
        learner_id: learnerId, kind, title: title.trim(),
        issuer: issuer.trim() || null,
        description: description.trim() || null,
        date_awarded: dateAwarded || null,
        location: location.trim() || null,
        url: url.trim() || null,
        skills: skills.split(",").map((s) => s.trim()).filter(Boolean),
        visibility, media_path, media_mime,
      };
      if (editing) {
        const { error } = await supabase.from("portfolio_items").update(payload).eq("id", editing.id);
        if (error) throw error;
        toast.success("Updated");
      } else {
        const { error } = await supabase.from("portfolio_items").insert(payload);
        if (error) throw error;
        toast.success("Added to portfolio");
      }
      onSaved(); onOpenChange(false);
    } catch (e) {
      toast.error((e as Error).message);
    } finally { setSaving(false); }
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
        <DialogHeader><DialogTitle>{editing ? "Edit item" : "Add to portfolio"}</DialogTitle></DialogHeader>
        <div className="space-y-3">
          <div>
            <Label>Type</Label>
            <Select value={kind} onValueChange={(v) => setKind(v as Kind)}>
              <SelectTrigger><SelectValue /></SelectTrigger>
              <SelectContent>{KINDS.map((k) => <SelectItem key={k.id} value={k.id}>{k.label.replace(/s$/, "")}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <div><Label>Title *</Label><Input value={title} onChange={(e) => setTitle(e.target.value)} placeholder="e.g. National Science Fair Winner" /></div>
          <div className="grid grid-cols-2 gap-3">
            <div><Label>Issuer / Organisation</Label><Input value={issuer} onChange={(e) => setIssuer(e.target.value)} /></div>
            <div><Label>Date</Label><Input type="date" value={dateAwarded} onChange={(e) => setDateAwarded(e.target.value)} /></div>
          </div>
          <div><Label>Description</Label><Textarea rows={3} value={description} onChange={(e) => setDescription(e.target.value)} /></div>
          <div className="grid grid-cols-2 gap-3">
            <div><Label>Location</Label><Input value={location} onChange={(e) => setLocation(e.target.value)} /></div>
            <div><Label>Link</Label><Input value={url} onChange={(e) => setUrl(e.target.value)} placeholder="https://" /></div>
          </div>
          <div><Label>Skills (comma separated)</Label><Input value={skills} onChange={(e) => setSkills(e.target.value)} placeholder="leadership, python, public speaking" /></div>
          <div>
            <Label>File / Media (optional)</Label>
            <Input type="file" accept="image/*,video/*,application/pdf" onChange={(e) => setFile(e.target.files?.[0] ?? null)} />
            {editing?.media_path && !file && <p className="text-xs text-muted-foreground mt-1">Existing file kept. Choose a new file to replace.</p>}
          </div>
          <div>
            <Label>Visibility</Label>
            <Select value={visibility} onValueChange={(v) => setVisibility(v as "private" | "school" | "public")}>
              <SelectTrigger><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="private">Private — only me</SelectItem>
                <SelectItem value="school">School — staff & parents</SelectItem>
                <SelectItem value="public">Public — anyone with the link</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </div>
        <DialogFooter>
          <Button variant="ghost" onClick={() => onOpenChange(false)}>Cancel</Button>
          <Button onClick={save} disabled={saving}>{saving && <Loader2 className="size-4 animate-spin mr-2" />}Save</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function ProfileDialog({ open, onOpenChange, settings, onSaved, learnerId }: {
  open: boolean; onOpenChange: (v: boolean) => void; settings: Settings | null; onSaved: (s: Settings) => void; learnerId: string;
}) {
  const [headline, setHeadline] = useState(""); const [bio, setBio] = useState("");
  const [email, setEmail] = useState(""); const [phone, setPhone] = useState("");
  const [website, setWebsite] = useState(""); const [linkedin, setLinkedin] = useState(""); const [github, setGithub] = useState("");
  const [slug, setSlug] = useState(""); const [isPublic, setIsPublic] = useState(false);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!open || !settings) return;
    setHeadline(settings.headline ?? ""); setBio(settings.bio ?? "");
    setEmail(settings.contact_email ?? ""); setPhone(settings.contact_phone ?? "");
    setWebsite(settings.website ?? ""); setLinkedin(settings.linkedin ?? ""); setGithub(settings.github ?? "");
    setSlug(settings.public_slug ?? ""); setIsPublic(settings.is_public);
  }, [open, settings]);

  async function save() {
    setSaving(true);
    try {
      const payload = {
        learner_id: learnerId,
        headline: headline.trim() || null,
        bio: bio.trim() || null,
        contact_email: email.trim() || null,
        contact_phone: phone.trim() || null,
        website: website.trim() || null,
        linkedin: linkedin.trim() || null,
        github: github.trim() || null,
        public_slug: slug.trim().toLowerCase().replace(/[^a-z0-9-]/g, "-") || null,
        is_public: isPublic,
      };
      const { data, error } = await supabase.from("portfolio_settings").upsert(payload, { onConflict: "learner_id" }).select("*").single();
      if (error) throw error;
      onSaved(data as Settings); toast.success("Profile saved"); onOpenChange(false);
    } catch (e) { toast.error((e as Error).message); } finally { setSaving(false); }
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
        <DialogHeader><DialogTitle>Portfolio profile</DialogTitle></DialogHeader>
        <div className="space-y-3">
          <div><Label>Headline</Label><Input value={headline} onChange={(e) => setHeadline(e.target.value)} placeholder="Aspiring engineer & community leader" /></div>
          <div><Label>Bio</Label><Textarea rows={4} value={bio} onChange={(e) => setBio(e.target.value)} /></div>
          <div className="grid grid-cols-2 gap-3">
            <div><Label>Email</Label><Input value={email} onChange={(e) => setEmail(e.target.value)} /></div>
            <div><Label>Phone</Label><Input value={phone} onChange={(e) => setPhone(e.target.value)} /></div>
            <div><Label>Website</Label><Input value={website} onChange={(e) => setWebsite(e.target.value)} /></div>
            <div><Label>LinkedIn</Label><Input value={linkedin} onChange={(e) => setLinkedin(e.target.value)} /></div>
            <div><Label>GitHub</Label><Input value={github} onChange={(e) => setGithub(e.target.value)} /></div>
            <div><Label>Public slug</Label><Input value={slug} onChange={(e) => setSlug(e.target.value)} placeholder="jane-doe" /></div>
          </div>
          <label className="flex items-center justify-between gap-3 rounded-lg border p-3">
            <div>
              <div className="font-medium text-sm">Public portfolio</div>
              <div className="text-xs text-muted-foreground">Share at /p/{slug || "your-slug"}. Only items marked Public are shown.</div>
            </div>
            <Switch checked={isPublic} onCheckedChange={setIsPublic} />
          </label>
        </div>
        <DialogFooter>
          <Button variant="ghost" onClick={() => onOpenChange(false)}>Cancel</Button>
          <Button onClick={save} disabled={saving}>{saving && <Loader2 className="size-4 animate-spin mr-2" />}Save</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
